import { UseFormReturn } from 'react-hook-form';
import { IColumn } from '@models/Forms/IForms';
import Field from '../Field/Field';
import Block from '../Block/Block';
import Table from '../Table/Table';
import './Column.scss';
import { IFieldElem } from '@models/IFormData';
import Dict from '../Dict/Dict';
import Row from '../Row/Row';
import Abook from '../Abook/Abook';
import Calc from '../Calc/Calc';
import Text from '../Text/Text';
import FormHint from '../Hint/FormHint';
import AutoComplete from '../AutoComplete/AutoComplete';
import { IUniqueKey } from '@molecules/formbuilder/interfaces/IUniqueKey';
import LinksTable from '@molecules/formbuilder/controls/LinksTable/LinksTable';
import AttachmentsTable from '@molecules/formbuilder/controls/AttachmentsTable/AttachmentsTable';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import CustomComponent from '@molecules/formbuilder/controls/CustomComponent/CustomComponent';
import Url from '../Url/Url';
import EditInModal from '../EditInModal/EditInModal';
import { useActivated, useFormulaState } from '@molecules/formbuilder/hooks';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { DocumentFormulaTraceParentContext, formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { useContext } from 'react';

export interface IColumnProps<TFieldValues extends object = object> extends IUniqueKey {
    column?: IColumn;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    docScheme?: IDocumentScheme;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const Column = <TFieldValues extends object = object>({
    column,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    uniqueKey,
    docScheme,
    ensureExternalDataSourceLoaded,
}: IColumnProps<TFieldValues>) => {
    const { activated } = useActivated();
    const traceParent = useContext(DocumentFormulaTraceParentContext);
    const visibility = useFormulaState(column?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: column?.id
            ? {
                  targetId: column.id,
                  targetKind: 'column',
                  ruleKind: 'visibility',
              }
            : undefined,
    });

    return column && visibility ? (
        <DocumentFormulaTraceParentContext
            value={column.id ? { targetId: column.id, targetKind: 'column' } : traceParent}
        >
            <div
                className="form-column"
                data-testid={column.id ? `column-${column.id}` : undefined}
                {...formulaTraceAttrs(column.id, 'column')}
            >
                {column.blocks?.map((item, i) => (
                    <Block
                        key={`${uniqueKey}block-${i}`}
                        block={item}
                        formMethods={formMethods}
                        docId={docId}
                        setError={setError}
                        isEdit={isEdit}
                        isNew={isNew}
                        turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                        fields={fields}
                        includedFields={includedFields}
                        uniqueKey={uniqueKey}
                        docScheme={docScheme}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.fields?.map((item, i) => (
                    <Field
                        key={`${uniqueKey}field-${i}`}
                        field={item}
                        formMethods={formMethods}
                        setError={setError}
                        isEdit={isEdit}
                        isNew={isNew}
                        turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                        fields={fields}
                        includedFields={includedFields}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.tables?.map((item, i) => (
                    <Table
                        table={item}
                        formMethods={formMethods}
                        docId={docId}
                        setError={setError}
                        isEdit={isEdit}
                        isNew={isNew}
                        turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                        fields={fields}
                        key={`${uniqueKey}table-${i}`}
                        includedFields={includedFields}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {docScheme?.links?.tables &&
                    column.linksTables?.map((item, i) => (
                        <LinksTable
                            table={item}
                            tables={docScheme.links.tables}
                            docId={docId}
                            key={`${uniqueKey}links-${i}`}
                        />
                    ))}
                {docScheme?.attach?.tables &&
                    column.attachmentsTables?.map((item, i) => (
                        <AttachmentsTable
                            table={item}
                            tables={docScheme.attach.tables}
                            formMethods={formMethods}
                            docId={docId}
                            isEdit={isEdit}
                            isNew={isNew}
                            fields={fields}
                            key={`${uniqueKey}attach-${i}`}
                        />
                    ))}
                {column.dicts?.map((item, i) => (
                    <Dict
                        dict={item}
                        docId={docId}
                        formMethods={formMethods}
                        setError={setError}
                        isEdit={isEdit}
                        isNew={isNew}
                        turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                        fields={fields}
                        key={`${uniqueKey}dict-${i}`}
                        includedFields={includedFields}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.hints?.map((item, i) => (
                    <FormHint
                        hint={item}
                        formMethods={formMethods}
                        isEdit={isEdit}
                        isNew={isNew}
                        fields={fields}
                        key={`${uniqueKey}hint-${i}`}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.texts?.map((item, i) => (
                    <Text
                        text={item}
                        formMethods={formMethods}
                        isEdit={isEdit}
                        isNew={isNew}
                        fields={fields}
                        key={`${uniqueKey}text-${i}`}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.urls?.map((item, i) => (
                    <Url
                        url={item}
                        formMethods={formMethods}
                        isEdit={isEdit}
                        isNew={isNew}
                        fields={fields}
                        key={`${uniqueKey}url-${i}`}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.editInModals?.map((item, i) => (
                    <EditInModal
                        editInModal={item}
                        docId={docId}
                        formMethods={formMethods}
                        isEdit={isEdit}
                        isNew={isNew}
                        fields={fields}
                        key={`${uniqueKey}editmodal-${i}`}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}

                {column.abooks?.map((item, i) => (
                    <Abook
                        abook={item}
                        formMethods={formMethods}
                        setError={setError}
                        isEdit={isEdit}
                        isNew={isNew}
                        turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                        fields={fields}
                        key={`${uniqueKey}abook-${i}`}
                        includedFields={includedFields}
                        docId={docId}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.rows?.map((item, i) => (
                    <Row
                        row={item}
                        formMethods={formMethods}
                        docId={docId}
                        setError={setError}
                        fields={fields}
                        isEdit={isEdit}
                        isNew={isNew}
                        turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                        key={`${uniqueKey}row-${i}`}
                        includedFields={includedFields}
                        uniqueKey={uniqueKey}
                        docScheme={docScheme}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.calcs?.map((item, i) => (
                    <Calc
                        calc={item}
                        formMethods={formMethods}
                        setError={setError}
                        fields={fields}
                        isEdit={isEdit}
                        isNew={isNew}
                        key={`${uniqueKey}calc-${i}`}
                        includedFields={includedFields}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {column.autoCompletes?.map((item, i) => (
                    <AutoComplete
                        autoComplete={item}
                        formMethods={formMethods}
                        setError={setError}
                        fields={fields}
                        isEdit={isEdit}
                        isNew={isNew}
                        key={`${uniqueKey}autocomplete-${i}`}
                        includedFields={includedFields}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />
                ))}
                {docId !== undefined &&
                    column.customComponents?.map((item, i) => (
                        <CustomComponent
                            component={item}
                            formMethods={formMethods}
                            docId={docId}
                            isEdit={isEdit}
                            isNew={isNew}
                            fields={fields}
                            key={`${uniqueKey}custom-${i}`}
                        />
                    ))}
            </div>
        </DocumentFormulaTraceParentContext>
    ) : (
        <></>
    );
};

export default Column;
